<template>
  <v-card class="rounded-lg white">
    <v-tabs v-model="activeTab" background-color="transparent">
      <v-tab>بسته‌های فروخته‌شده</v-tab>
      <v-tab>درآمد فروش</v-tab>
      <v-tab>گزارش تخفیف‌ها</v-tab>
    </v-tabs>

    <v-divider></v-divider>

    <v-tabs-items v-model="activeTab">
      <!-- ============ TAB 1: existing counts report (unchanged behaviour) ============ -->
      <v-tab-item>
        <v-card-title class="secondary--text">
          گزارش فروش بسته‌ها
          <v-spacer></v-spacer>

          <v-col cols="2">
            <v-select
              label="از ماه"
              class="rounded-lg"
              :items="monthItems"
              v-model="rangeFilters.fromMonth"
              item-text="text"
              item-value="value"
              clearable
            />
          </v-col>
          <v-col cols="2">
            <v-text-field
              label="از سال"
              class="rounded-lg"
              type="number"
              v-model.number="rangeFilters.fromYear"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              label="تا ماه"
              class="rounded-lg"
              :items="monthItems"
              v-model="rangeFilters.toMonth"
              item-text="text"
              item-value="value"
              clearable
            />
          </v-col>
          <v-col cols="2">
            <v-text-field
              label="تا سال"
              class="rounded-lg"
              type="number"
              v-model.number="rangeFilters.toYear"
            />
          </v-col>
          <v-col cols="3">
            <v-select
              label="نوع تجمیع"
              class="rounded-lg"
              :items="aggregationItems"
              v-model="rangeFilters.aggregation"
              item-text="text"
              item-value="value"
            />
          </v-col>
          <v-btn color="primary" class="ml-2" :loading="loading" @click="fetchReport">
            اعمال فیلتر
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-alert type="info" text dense class="mb-4">
            درصدها بر اساس مبلغ نهایی پرداخت‌شده توسط کاربر محاسبه شده‌اند (نه تعداد و نه قیمت لیست)؛
            درصد هر بسته از مجموع فروش بسته‌ها و درصد هر دوره پرداخت از کل فروش همان بازه است.
          </v-alert>

          <m-datatable
            :show-select="false"
            :show-filter-row="false"
            :headers="countHeaders"
            :items="periods"
            :loading="loading"
            item-key="period"
          >
            <template #item.label="{ item }">{{ formatLabel(item) }}</template>
            <template #item.count="{ item }"><strong>{{ item.count }}</strong></template>
          </m-datatable>
        </v-card-text>
      </v-tab-item>

      <!-- ============ TAB 2: revenue -- reuses the SAME data/filters as tab 1 ============ -->
      <v-tab-item>
        <v-card-title class="secondary--text">
          درآمد فروش
          <v-spacer></v-spacer>

          <v-col cols="2">
            <v-select
              label="از ماه"
              class="rounded-lg"
              :items="monthItems"
              v-model="rangeFilters.fromMonth"
              item-text="text"
              item-value="value"
              clearable
            />
          </v-col>
          <v-col cols="2">
            <v-text-field
              label="از سال"
              class="rounded-lg"
              type="number"
              v-model.number="rangeFilters.fromYear"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              label="تا ماه"
              class="rounded-lg"
              :items="monthItems"
              v-model="rangeFilters.toMonth"
              item-text="text"
              item-value="value"
              clearable
            />
          </v-col>
          <v-col cols="2">
            <v-text-field
              label="تا سال"
              class="rounded-lg"
              type="number"
              v-model.number="rangeFilters.toYear"
            />
          </v-col>
          <v-col cols="3">
            <v-select
              label="نوع تجمیع"
              class="rounded-lg"
              :items="aggregationItems"
              v-model="rangeFilters.aggregation"
              item-text="text"
              item-value="value"
            />
          </v-col>
          <v-btn color="primary" class="ml-2" :loading="loading" @click="fetchReport">
            اعمال فیلتر
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-alert type="info" text dense class="mb-4">
            «خالص (واقعی)» یعنی مبلغ فاکتور منهای تخفیف، بدون احتساب ۱۰٪ مالیات ارزش‌افزوده.
            فاکتورهایی که مبلغ پرداختی نهایی‌شان صفر بوده یا تخفیف ۱۰۰٪ داشته‌اند، در همه‌ی
            اعداد این صفحه نادیده گرفته شده‌اند.
          </v-alert>

          <m-datatable
            :show-select="false"
            :show-filter-row="false"
            :headers="revenueHeaders"
            :items="periods"
            :loading="loading"
            item-key="period"
            class="mb-6"
          >
            <template #item.label="{ item }">{{ formatLabel(item) }}</template>
            <template #item.gross_amount="{ item }">{{ formatAmount(item.gross_amount) }}</template>
            <template #item.discount_amount="{ item }">{{ formatAmount(item.discount_amount) }}</template>
            <template #item.net_amount="{ item }">
              <strong>{{ formatAmount(item.net_amount) }}</strong>
            </template>
            <template #item.tax_amount="{ item }">{{ formatAmount(item.tax_amount) }}</template>
            <template #item.payable_amount="{ item }">{{ formatAmount(item.payable_amount) }}</template>
          </m-datatable>

          <v-expansion-panels flat multiple>
            <v-expansion-panel>
              <v-expansion-panel-header>
                درآمد به تفکیک بسته (ناخالص، پیش از تخفیف)
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <m-datatable
                  :show-select="false"
                  :show-filter-row="false"
                  :headers="packageAmountHeaders"
                  :items="periods"
                  :loading="loading"
                  item-key="period"
                >
                  <template #item.label="{ item }">{{ formatLabel(item) }}</template>
                </m-datatable>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>درآمد به تفکیک نوع فروش</v-expansion-panel-header>
              <v-expansion-panel-content>
                <m-datatable
                  :show-select="false"
                  :show-filter-row="false"
                  :headers="itemTypeAmountHeaders"
                  :items="periods"
                  :loading="loading"
                  item-key="period"
                >
                  <template #item.label="{ item }">{{ formatLabel(item) }}</template>
                </m-datatable>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>
                درآمد به تفکیک دوره پرداخت (خالص از تخفیف)
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <m-datatable
                  :show-select="false"
                  :show-filter-row="false"
                  :headers="billingCycleAmountHeaders"
                  :items="periods"
                  :loading="loading"
                  item-key="period"
                >
                  <template #item.label="{ item }">{{ formatLabel(item) }}</template>
                </m-datatable>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-tab-item>

      <!-- ============ TAB 3: discounts -- own filters, defaults to current month ============ -->
      <v-tab-item>
        <v-card-title class="secondary--text">
          گزارش تخفیف‌ها
          <v-spacer></v-spacer>

          <v-col cols="2">
            <v-select
              label="از ماه"
              class="rounded-lg"
              :items="monthItems"
              v-model="discountFilters.fromMonth"
              item-text="text"
              item-value="value"
              clearable
            />
          </v-col>
          <v-col cols="2">
            <v-text-field
              label="از سال"
              class="rounded-lg"
              type="number"
              v-model.number="discountFilters.fromYear"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              label="تا ماه"
              class="rounded-lg"
              :items="monthItems"
              v-model="discountFilters.toMonth"
              item-text="text"
              item-value="value"
              clearable
            />
          </v-col>
          <v-col cols="2">
            <v-text-field
              label="تا سال"
              class="rounded-lg"
              type="number"
              v-model.number="discountFilters.toYear"
            />
          </v-col>
          <v-col cols="3">
            <v-select
              label="نوع تجمیع"
              class="rounded-lg"
              :items="aggregationItems"
              v-model="discountFilters.aggregation"
              item-text="text"
              item-value="value"
            />
          </v-col>
          <v-btn color="primary" class="ml-2" :loading="discountLoading" @click="fetchDiscountReport">
            اعمال فیلتر
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-alert type="info" text dense class="mb-4">
            به‌صورت پیش‌فرض روی ماه جاری (تا زمانی که «از ماه/سال» و «تا ماه/سال» را خالی بگذارید) نمایش
            داده می‌شود؛ کاملاً قابل تغییر به هر بازه یا نوع تجمیع دیگری (از جمله فصلی) است.
          </v-alert>

          <m-datatable
            :show-select="false"
            :show-filter-row="false"
            :headers="discountHeaders"
            :items="discountPeriods"
            :loading="discountLoading"
            item-key="period"
          >
            <template #item.label="{ item }">{{ formatLabel(item) }}</template>
            <template #item.discount_amount="{ item }">
              <strong>{{ formatAmount(item.discount_amount) }}</strong>
            </template>
            <template #item.discount_ratio="{ item }">
              {{ formatPercent(item.discount_amount, item.gross_amount) }}
            </template>
          </m-datatable>
        </v-card-text>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
// Jalali month names, index 0 == Farvardin (month 1)
const JALALI_MONTHS = [
  "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
  "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
];
const JALALI_SEASONS = ["بهار", "تابستان", "پاییز", "زمستان"];

// Must match subscription.models.Extension.PACKAGES codenames
const PACKAGE_LABELS = {
  ONE: "رایگان",
  TWO: "پایه",
  THREE: "هوشمند",
  FOUR: "قدرتمند",
};

// Must match subscription.models.FactorItem.TYPES codes
const ITEM_TYPE_LABELS = {
  be: "خرید جدید",
  ee: "تمدید",
  up: "ارتقاء",
};

// Must match subscription.models.Factor.BILLING_CYCLES codes
const BILLING_CYCLE_LABELS = {
  monthly: "ماهانه",
  six_monthly: "شش‌ماهه",
  yearly: "سالانه",
  permanent: "دائمی",
};

function makeRangeFilters(aggregation) {
  // fromMonth/fromYear/toMonth/toYear start empty on purpose: the backend
  // defaults these -- to the current Jalali year for the main report, or
  // to the current Jalali month for the discount report (see
  // fetchDiscountReport's `default_range` param below).
  return {
    fromMonth: null,
    fromYear: null,
    toMonth: null,
    toYear: null,
    aggregation,
  };
}

export default {
  name: "SoldPackagesReport",
  data() {
    return {
      activeTab: 0,
      loading: false,
      discountLoading: false,
      periods: [],
      discountPeriods: [],
      monthItems: JALALI_MONTHS.map((text, i) => ({ text, value: i + 1 })),
      aggregationItems: [
        { text: "ماهانه", value: "monthly" },
        { text: "فصلی", value: "seasonally" },
        { text: "ششماهه", value: "semi_annually" },
        { text: "سالانه", value: "yearly" },
      ],
      rangeFilters: makeRangeFilters("monthly"),
      discountFilters: makeRangeFilters("monthly"),
    };
  },
  computed: {
    countHeaders() {
      const headers = [
        { text: "دوره", value: "label", filterable: false, sortable: false },
      ];
      // per-package: count + % of package revenue, based on final paid price
      Object.keys(PACKAGE_LABELS).forEach((code) => {
        headers.push({
          text: PACKAGE_LABELS[code],
          value: `package_${code}`,
          filterable: false,
          getValue: (item) =>
            this.formatCountWithPercent(
              item.package_counts[code] || 0,
              item.package_paid_amounts[code] || 0,
              this.sumValues(item.package_paid_amounts)
            ),
        });
      });
      headers.push({
        text: "مجموع",
        value: "count",
        type: "numeric",
        filterable: false,
        sortable: false,
      });
      // per-billing-cycle: count + % of total sales, based on final paid price
      Object.keys(BILLING_CYCLE_LABELS).forEach((code) => {
        headers.push({
          text: BILLING_CYCLE_LABELS[code],
          value: `cycle_${code}`,
          filterable: false,
          getValue: (item) =>
            this.formatCountWithPercent(
              item.billing_cycle_counts[code] || 0,
              item.billing_cycle_paid_amounts[code] || 0,
              item.payable_amount
            ),
        });
      });
      return headers;
    },
    revenueHeaders() {
      return [
        { text: "دوره", value: "label", filterable: false, sortable: false },
        { text: "ناخالص", value: "gross_amount", type: "numeric", filterable: false },
        { text: "تخفیف", value: "discount_amount", type: "numeric", filterable: false },
        { text: "خالص (واقعی)", value: "net_amount", type: "numeric", filterable: false },
        { text: "مالیات ارزش‌افزوده", value: "tax_amount", type: "numeric", filterable: false },
        { text: "مبلغ پرداختی نهایی", value: "payable_amount", type: "numeric", filterable: false },
      ];
    },
    packageAmountHeaders() {
      const headers = [
        { text: "دوره", value: "label", filterable: false, sortable: false },
      ];
      // ONE (free tier) is always 0 revenue -- skipped here on purpose.
      ["TWO", "THREE", "FOUR"].forEach((code) => {
        headers.push({
          text: PACKAGE_LABELS[code],
          value: `pkg_amount_${code}`,
          type: "numeric",
          filterable: false,
          getValue: (item) => this.formatAmount(item.package_amounts[code] || 0),
        });
      });
      return headers;
    },
    itemTypeAmountHeaders() {
      const headers = [
        { text: "دوره", value: "label", filterable: false, sortable: false },
      ];
      Object.keys(ITEM_TYPE_LABELS).forEach((code) => {
        headers.push({
          text: ITEM_TYPE_LABELS[code],
          value: `type_amount_${code}`,
          type: "numeric",
          filterable: false,
          getValue: (item) => this.formatAmount(item.item_type_amounts[code] || 0),
        });
      });
      return headers;
    },
    billingCycleAmountHeaders() {
      const headers = [
        { text: "دوره", value: "label", filterable: false, sortable: false },
      ];
      Object.keys(BILLING_CYCLE_LABELS).forEach((code) => {
        headers.push({
          text: BILLING_CYCLE_LABELS[code],
          value: `cycle_amount_${code}`,
          type: "numeric",
          filterable: false,
          getValue: (item) => this.formatAmount(item.billing_cycle_amounts[code] || 0),
        });
      });
      return headers;
    },
    discountHeaders() {
      return [
        { text: "دوره", value: "label", filterable: false, sortable: false },
        { text: "مبلغ تخفیف", value: "discount_amount", type: "numeric", filterable: false },
        {
          text: "تعداد فاکتور تخفیف‌دار",
          value: "discounted_count",
          type: "numeric",
          filterable: false,
        },
        {
          text: "نسبت به فروش ناخالص",
          value: "discount_ratio",
          filterable: false,
          sortable: false,
        },
      ];
    },
  },
  created() {
    this.fetchReport();
    this.fetchDiscountReport();
  },
  methods: {
    formatLabel(item) {
      // monthly rows carry `month`; seasonal rows encode the season in
      // `label` (e.g. "1405-Q2"); semi_annually rows use H1/H2; yearly
      // rows have neither.
      if (item.month) {
        return `${JALALI_MONTHS[item.month - 1]} ${item.year}`;
      }
      if (item.label.endsWith("H1")) return `نیمه اول ${item.year}`;
      if (item.label.endsWith("H2")) return `نیمه دوم ${item.year}`;
      const seasonMatch = item.label.match(/Q([1-4])$/);
      if (seasonMatch) {
        return `${JALALI_SEASONS[Number(seasonMatch[1]) - 1]} ${item.year}`;
      }
      return `${item.year}`;
    },
    formatAmount(value) {
      if (value === null || value === undefined) return "۰";
      return Math.round(value).toLocaleString("fa-IR");
    },
    sumValues(dict) {
      return Object.values(dict || {}).reduce((sum, v) => sum + (v || 0), 0);
    },
    // "count (percent٪)" -- percent is always based on the final price the
    // customer paid (payableAmount), never on list price or count share.
    formatCountWithPercent(count, paidAmount, totalPaidAmount) {
      const countText = Number(count || 0).toLocaleString("fa-IR");
      if (!totalPaidAmount) return countText;
      const percent = ((paidAmount || 0) / totalPaidAmount) * 100;
      return `${countText} (${percent.toFixed(1)}٪)`;
    },
    formatPercent(numerator, denominator) {
      if (!denominator) return "—";
      return `${((numerator / denominator) * 100).toFixed(1)}٪`;
    },
    buildParams(filters) {
      const params = { aggregation: filters.aggregation };
      if (filters.fromMonth) params.from_month = filters.fromMonth;
      if (filters.fromYear) params.from_year = filters.fromYear;
      if (filters.toMonth) params.to_month = filters.toMonth;
      if (filters.toYear) params.to_year = filters.toYear;
      return params;
    },
    fetchReport() {
      this.loading = true;
      this.request({
        url: this.endpoint("sobhanAdmin/sold-packages-report"),
        method: "GET",
        params: this.buildParams(this.rangeFilters),
        success: (data) => {
          this.periods = data;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
    },
    fetchDiscountReport() {
      this.discountLoading = true;
      const params = this.buildParams(this.discountFilters);
      // Ask the backend to default to "this month" when a field is left
      // empty, instead of "this year" (which the other two tabs use).
      params.default_range = "current_month";
      this.request({
        url: this.endpoint("sobhanAdmin/sold-packages-report"),
        method: "GET",
        params,
        success: (data) => {
          this.discountPeriods = data;
          this.discountLoading = false;
        },
        error: () => {
          this.discountLoading = false;
        },
      });
    },
  },
};
</script>

<style scoped lang="scss"></style>